import { useCallback } from 'react'
import { authenticatedFetchJson } from '../services/api/apiClient'
import { apiEndpoints } from '../consts'

export const useSmsAutomationAPI = (userID, actions) => {
  const fetchSmsAutomations = useCallback(async (page = 1, limit = 50) => {
    try {
      actions.setLoading(true)
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        type: 'sms_on_disposition'
      })
      const data = await authenticatedFetchJson(`${apiEndpoints.automations}?${params}`)
      actions.setAutomations(data.data || [])
      return data.meta
    } catch (err) {
      actions.setError('Error loading SMS automations: ' + err.message)
      return null
    } finally {
      actions.setLoading(false)
    }
  }, [actions])

  const fetchDispositions = useCallback(async () => {
    try {
      const data = await authenticatedFetchJson(apiEndpoints.callsFilters)
      const dispositions = data.data?.dispositions || data.dispositions || []
      actions.setDispositions(dispositions)
    } catch (err) {
      actions.setError('Error loading dispositions: ' + err.message)
    }
  }, [actions])

  const fetchCampaignsForAutomation = useCallback(async (automationId) => {
    try {
      const data = await authenticatedFetchJson(apiEndpoints.automationCampaigns(automationId))
      actions.setCampaigns(data.data || [])
    } catch (err) {
      actions.setError('Error loading campaigns: ' + err.message)
    }
  }, [actions])

  const fetchAllCampaigns = useCallback(async () => {
    try {
      const data = await authenticatedFetchJson(apiEndpoints.automationsCampaigns)
      actions.setCampaigns(data.data || [])
    } catch (err) {
      actions.setError('Error loading campaigns: ' + err.message)
    }
  }, [actions])

  const createAutomation = useCallback(async (automation) => {
    try {
      const automationData = {
        ...automation,
        type: 'sms_on_disposition',
        created_by: userID
      }

      await authenticatedFetchJson(apiEndpoints.automations, {
        method: 'POST',
        body: JSON.stringify(automationData)
      })

      await fetchSmsAutomations()
      return true
    } catch (err) {
      actions.setError('Error creating automation: ' + err.message)
      return false
    }
  }, [userID, actions, fetchSmsAutomations])

  const updateAutomation = useCallback(async (automation) => {
    try {
      await authenticatedFetchJson(apiEndpoints.automation(automation.id), {
        method: 'PUT',
        body: JSON.stringify(automation)
      })

      await fetchSmsAutomations()
      return true
    } catch (err) {
      actions.setError('Error updating automation: ' + err.message)
      return false
    }
  }, [actions, fetchSmsAutomations])

  const deleteAutomation = useCallback(async (automationId) => {
    try {
      await authenticatedFetchJson(apiEndpoints.automation(automationId), {
        method: 'DELETE'
      })

      await fetchSmsAutomations()
      return true
    } catch (err) {
      actions.setError('Error deleting automation: ' + err.message)
      return false
    }
  }, [actions, fetchSmsAutomations])

  const toggleAutomation = useCallback(async (automationId, enabled) => {
    try {
      actions.updateAutomationEnabled(automationId, enabled)

      await authenticatedFetchJson(apiEndpoints.automation(automationId), {
        method: 'PUT',
        body: JSON.stringify({ enabled })
      })

      return true
    } catch (err) {
      actions.updateAutomationEnabled(automationId, !enabled)
      actions.setError('Error toggling automation: ' + err.message)
      return false
    }
  }, [actions])

  const testSms = useCallback(async (automationId, phoneNumber) => {
    try {
      const response = await authenticatedFetchJson(apiEndpoints.automationTestSms(automationId), {
        method: 'POST',
        body: JSON.stringify({ phoneNumber })
      })

      return response.data
    } catch (err) {
      actions.setError('Error sending test SMS: ' + err.message)
      return { success: false, error: err.message }
    }
  }, [actions])

  const fetchSmsHistory = useCallback(async (automationId, page = 1, limit = 25) => {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString()
      })
      const data = await authenticatedFetchJson(`${apiEndpoints.automationSmsHistory(automationId)}?${params}`)
      actions.setSmsHistory(data.data || [])
      actions.setSmsHistoryMeta(data.meta || { total: 0, page: 1, totalPages: 1 })
      return data
    } catch (err) {
      actions.setError('Error loading SMS history: ' + err.message)
      return null
    }
  }, [actions])

  const toggleCampaign = useCallback(async (automationId, campaignName, enabled) => {
    try {
      await authenticatedFetchJson(apiEndpoints.automationCampaign(automationId, campaignName), {
        method: 'PUT',
        body: JSON.stringify({ enabled })
      })

      await fetchCampaignsForAutomation(automationId)
      return true
    } catch (err) {
      actions.setError('Error updating campaign: ' + err.message)
      return false
    }
  }, [actions, fetchCampaignsForAutomation])

  const fetchSmsSettings = useCallback(async () => {
    try {
      const data = await authenticatedFetchJson(apiEndpoints.automationsSmsSettings)
      actions.setSmsSettings(data.data || { override_enabled: false, override_to_number: '' })
    } catch (err) {
      actions.setError('Error loading SMS settings: ' + err.message)
    }
  }, [actions])

  const updateSmsSettings = useCallback(async (settings) => {
    try {
      const data = await authenticatedFetchJson(apiEndpoints.automationsSmsSettings, {
        method: 'PUT',
        body: JSON.stringify(settings)
      })
      actions.setSmsSettings(data.data)
      return true
    } catch (err) {
      actions.setError('Error updating SMS settings: ' + err.message)
      return false
    }
  }, [actions])

  const toggleAllCampaigns = useCallback(async (automationId, enabled) => {
    try {
      await authenticatedFetchJson(apiEndpoints.automationCampaignsBulkToggle(automationId), {
        method: 'POST',
        body: JSON.stringify({ enabled })
      })

      await fetchCampaignsForAutomation(automationId)
      return true
    } catch (err) {
      actions.setError('Error toggling all campaigns: ' + err.message)
      return false
    }
  }, [actions, fetchCampaignsForAutomation])

  return {
    fetchSmsAutomations,
    fetchDispositions,
    fetchAllCampaigns,
    fetchCampaignsForAutomation,
    createAutomation,
    updateAutomation,
    deleteAutomation,
    toggleAutomation,
    testSms,
    fetchSmsHistory,
    toggleCampaign,
    toggleAllCampaigns,
    fetchSmsSettings,
    updateSmsSettings
  }
}

export default useSmsAutomationAPI
